import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { IndianRupee, Trash2, Upload, Plus, Recycle, Download } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { api, errMsg, fmtINR, downloadBlob } from "../../lib/api";
import { ArvMatrixTab } from "../../components/ArvMatrixTab";
import { PenaltyDiscountRules } from "../../components/PenaltyDiscountRules";
import { firstAllowedTab, useRoleModules } from "../../lib/roleModules";
import { NoAccessPanel, ReadOnlyNotice, ViewOnlyBadge } from "../../components/FeatureGate";

function FeeRatesTab({ code, fyId, readOnly = false }) {
  const [rates, setRates] = useState(null);
  const [cats, setCats] = useState([]);
  const [subs, setSubs] = useState([]);
  const [form, setForm] = useState({ category_id: "", sub_category_id: "", monthly_rate: "" });
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(() => {
    api.get(`/t/${code}/rate-engine/user-fee-rates`, { params: { financial_year_id: fyId } })
      .then((r) => setRates(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/t/${code}/masters/categories`).then((r) => setCats(r.data)).catch(() => {});
    api.get(`/t/${code}/masters/sub-categories`).then((r) => setSubs(r.data)).catch(() => {});
  }, [code, fyId]);
  useEffect(() => { if (fyId) load(); }, [load, fyId]);

  const add = async () => {
    if (!form.category_id || !form.monthly_rate) return toast.error("Category and monthly rate are required");
    try {
      const r = await api.post(`/t/${code}/rate-engine/user-fee-rates`, {
        financial_year_id: fyId, category_id: form.category_id,
        sub_category_id: form.sub_category_id || null, monthly_rate: Number(form.monthly_rate) });
      toast.success(`Fee saved • yearly ${fmtINR(r.data.yearly_rate)}`);
      setForm({ category_id: "", sub_category_id: "", monthly_rate: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const del = async (id) => {
    try { await api.delete(`/t/${code}/rate-engine/user-fee-rates/${id}`); toast.success("Rate deleted"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const doImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/t/${code}/rate-engine/user-fee-rates/import?financial_year_id=${fyId}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.imported} rates (${r.data.categories_created} categories, ${r.data.sub_categories_created} sub-categories created)`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!rates) return <Spinner label="Loading fee rates..." />;
  const subsFor = (cid) => subs.filter((s) => s.category_id === cid);

  return (
    <div className="space-y-4">
      {readOnly && <ReadOnlyNotice what="user fee rates" testId="fee-rates-readonly-notice" />}
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-base font-heading flex items-center gap-2"><Recycle className="h-4 w-4" /> User fee rates (category / sub-category)</CardTitle>
              <CardDescription>Monthly rate recorded; yearly auto-calculated (×12). {rates.length} rates configured.</CardDescription>
            </div>
            {!readOnly && <label>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={doImport} data-testid="fee-import-file-input" />
              <Button variant="outline" size="sm" asChild disabled={importing}>
                <span className="cursor-pointer" data-testid="fee-import-button"><Upload className="h-3.5 w-3.5 mr-1.5" /> {importing ? "Importing..." : "Import rate list (Excel)"}</span>
              </Button>
            </label>}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!readOnly && <div className="border rounded-lg p-3 grid grid-cols-1 sm:grid-cols-4 gap-2 items-end bg-muted/40" data-testid="fee-add-form">
            <div className="space-y-1">
              <Label className="text-xs">Category</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v, sub_category_id: "" })}>
                <SelectTrigger className="h-9" data-testid="fee-add-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.category_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Sub-category</Label>
              <Select value={form.sub_category_id} onValueChange={(v) => setForm({ ...form, sub_category_id: v })} disabled={!form.category_id}>
                <SelectTrigger className="h-9" data-testid="fee-add-sub-select"><SelectValue placeholder="Sub-category" /></SelectTrigger>
                <SelectContent>{subsFor(form.category_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.sub_category_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Monthly rate (₹)</Label>
              <Input type="number" className="h-9" value={form.monthly_rate}
                onChange={(e) => setForm({ ...form, monthly_rate: e.target.value })} data-testid="fee-add-monthly-input" />
            </div>
            <Button size="sm" onClick={add} data-testid="fee-add-save-button"><Plus className="h-3.5 w-3.5 mr-1" /> Add / update</Button>
          </div>}

          {rates.length === 0 ? (
            <EmptyState title="No user fee rates yet" subtitle="Import the SWM rate list Excel or add rates manually." />
          ) : (
            <div className="max-h-[480px] overflow-y-auto border rounded-lg">
              <Table data-testid="fee-rates-table">
                <TableHeader className="sticky top-0 bg-card">
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead>Sub-category</TableHead>
                    <TableHead className="text-right">Monthly</TableHead>
                    <TableHead className="text-right">Yearly (×12)</TableHead>
                    {!readOnly && <TableHead></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rates.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.category_name}</TableCell>
                      <TableCell className="text-muted-foreground">{r.sub_category_name || "-"}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtINR(r.monthly_rate)}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{fmtINR(r.yearly_rate)}</TableCell>
                      {!readOnly && <TableCell className="text-right">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => del(r.id)}
                          data-testid={`fee-delete-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Phase 152: Rate Engine tabs <-> "rate_engine" module sub-features
const TABS = [
  { value: "pt", feature: "pt_rates", label: "Property Tax (ARV Matrix)", testId: "tab-pt-rates" },
  { value: "fees", feature: "user_fee_rates", label: "User Fee Rates", testId: "tab-fee-rates" },
  { value: "pd", feature: "penalty_discount", label: "Penalty & Discount", testId: "tab-penalty-discount" },
];

export default function RateEngine() {
  const { code } = useParams();
  const [fys, setFys] = useState([]);
  const [fyId, setFyId] = useState("");
  const access = useRoleModules(code);
  const visibleTabs = TABS.filter((t) => access.featureAllowed("rate_engine", t.feature));
  const [tab, setTab] = useState("pt");
  useEffect(() => {
    if (!access.loaded) return;
    if (!visibleTabs.some((t) => t.value === tab)) { const f = firstAllowedTab(access, "rate_engine", TABS); if (f) setTab(f); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access.loaded, visibleTabs.length]);

  useEffect(() => {
    api.get(`/t/${code}/masters/financial-years`).then((r) => {
      setFys(r.data);
      const cur = r.data.find((f) => f.is_current);
      if (cur) setFyId(cur.id);
    }).catch((e) => toast.error(errMsg(e)));
  }, [code]);

  return (
    <div>
      <PageHeader title="Rate Engine" subtitle="Master rule engine for property tax and user fee pricing">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-accent"><IndianRupee className="h-3 w-3 mr-1" /> Monthly calc • yearly collection</Badge>
          <Select value={fyId} onValueChange={setFyId}>
            <SelectTrigger className="h-9 w-36" data-testid="rate-engine-fy-select"><SelectValue placeholder="FY" /></SelectTrigger>
            <SelectContent>{fys.map((f) => <SelectItem key={f.id} value={f.id}>{f.fy_label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </PageHeader>
      {access.loaded && visibleTabs.length === 0 ? <NoAccessPanel what="Rate Engine" testId="rate-engine-no-access" /> : (
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {visibleTabs.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={t.testId}>
              {t.label}{!access.canWrite("rate_engine", t.feature) && <ViewOnlyBadge />}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="pt" className="mt-4">{fyId && <ArvMatrixTab code={code} fyId={fyId} readOnly={!access.canWrite("rate_engine", "pt_rates")} />}</TabsContent>
        <TabsContent value="fees" className="mt-4">{fyId && <FeeRatesTab code={code} fyId={fyId} readOnly={!access.canWrite("rate_engine", "user_fee_rates")} />}</TabsContent>
        <TabsContent value="pd" className="mt-4"><PenaltyDiscountRules code={code} readOnly={!access.canWrite("rate_engine", "penalty_discount")} /></TabsContent>
      </Tabs>
      )}
    </div>
  );
}
